import { useMatch } from 'react-router-dom'

import {
  A3IncidenceFinanceCostCenterAssignmentFragment,
  IncidencePersonalHiringDetailsFragment,
  PayrollLegalEntityChangeInfoEmployeeGroupFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import { useIncidenceDetail } from 'modules/bookkeepers/lib/IncidenceDetailContext'
import {
  isContractType,
  isCostCenterType,
  isEmployeeDataType,
  isHiringType,
  isLeaveType,
  isLegalEntityType,
  isManualType,
  isReactivationType,
  isTerminationType,
} from 'modules/payroll_bundle/lib/Incidences/TypeHelpers'
import { InfoCallout } from 'modules/payroll_bundle/pages/IncidenceDetailPage/_InfoCallout'

import AdditionalSupplement from './_AdditionalSupplement'
import ContractInfo from './_ContractInfo'
import { CostCenterChangeInfo } from './_CostCenterChangeInfo'
import EmployeeDataChangeInfo from './_EmployeeDataChangeInfo'
import GenericIncidenceInfo from './_GenericIncidenceInfo'
import HiringInfo from './_HiringInfo'
import { LeaveContent } from './_LeaveContent'
import LegalEntityChangeInfo from './_LegalEntityChangeInfo'
import ManualIncidenceInfo from './_ManualIncidenceInfo'
import ReactivationInfo from './_ReactivationInfo'
import TerminationInfo from './_TerminationInfo'

interface Props {
  incidenceType: string
  employeeGroups: PayrollLegalEntityChangeInfoEmployeeGroupFragment[]
  listOfCostCenters: A3IncidenceFinanceCostCenterAssignmentFragment[]
  personalHiringDetails: IncidencePersonalHiringDetailsFragment[]
  hasA3innuvaFeature: boolean
  canRequestEau: boolean
  application: string
}

const IncidenceContent = ({
  incidenceType,
  employeeGroups,
  listOfCostCenters,
  personalHiringDetails,
  hasA3innuvaFeature,
  canRequestEau,
  application,
}: Props) => {
  const { navigation } = useNavigation()
  const { incidenceDetailModel, incidenceChangesCollection, incidenceFilesCollection } =
    useIncidenceDetail()
  const isBookkeeperView = !!useMatch({ path: navigation.bookkeepers.fullPath(), end: false })
  const incidence = useCurrentIncidence()

  if (isLeaveType(incidenceType)) {
    return (
      <LeaveContent
        incidenceType={incidenceType}
        incidenceDetailModel={incidenceDetailModel}
        incidenceFilesCollection={incidenceFilesCollection}
        incidenceChangesCollection={incidenceChangesCollection}
        canRequestEau={canRequestEau}
        application={application}
      />
    )
  }

  if (isTerminationType(incidenceType)) {
    if (incidenceDetailModel.attributes.terminated_on) {
      return (
        <>
          <TerminationInfo incidenceDetailModel={incidenceDetailModel} />
          <AdditionalSupplement incidence={incidence} isBookkeeperView={isBookkeeperView} />
        </>
      )
    }
    return <InfoCallout label={i18n.t('bookkeepers.incidence.details.deleted_termination')} />
  }

  if (isCostCenterType(incidenceType)) {
    const targetAssignment = listOfCostCenters.find((cc) => cc.id === incidence.targetId)
    if (!targetAssignment || !targetAssignment.costCenter) {
      return <InfoCallout label={i18n.t('bookkeepers.incidence.details.deleted_cost_center')} />
    }
    return (
      <CostCenterChangeInfo
        costCenters={listOfCostCenters}
        incidenceStartDate={incidence?.startsOn ?? ''}
        targetAssignmentId={incidence.targetId}
      />
    )
  }

  if (isContractType(incidenceType)) {
    if (incidenceDetailModel.id) {
      return (
        <ContractInfo
          incidenceDetailModel={incidenceDetailModel}
          incidenceChangesCollection={incidenceChangesCollection}
          isBookkeeperView={isBookkeeperView}
        />
      )
    }
    return <InfoCallout label={i18n.t('bookkeepers.incidence.details.deleted_contract_version')} />
  }

  if (isHiringType(incidenceType)) {
    return (
      <HiringInfo
        incidenceDetailModel={incidenceDetailModel}
        costCenters={listOfCostCenters}
        isBookkeeperView={isBookkeeperView}
        personalHiringDetails={personalHiringDetails}
        hasA3innuvaFeature={hasA3innuvaFeature}
      />
    )
  }

  if (isReactivationType(incidenceType)) {
    return (
      <ReactivationInfo
        incidenceDetailModel={incidenceDetailModel}
        hasA3innuvaFeature={hasA3innuvaFeature}
        isBookkeeperView={isBookkeeperView}
      />
    )
  }

  if (isManualType(incidenceType)) {
    return <ManualIncidenceInfo incidenceDetailModel={incidenceDetailModel} />
  }

  if (isLegalEntityType(incidenceType)) {
    return (
      <LegalEntityChangeInfo
        incidenceDetailModel={incidenceDetailModel}
        incidenceChangesCollection={incidenceChangesCollection}
        effective_date={incidence.startsOn}
        employeeGroup={employeeGroups}
      />
    )
  }

  if (isEmployeeDataType(incidenceType)) {
    return (
      <EmployeeDataChangeInfo
        incidenceDetailModel={incidenceDetailModel}
        incidenceChangesCollection={incidenceChangesCollection}
        incidenceType={incidenceType}
      />
    )
  }

  return <GenericIncidenceInfo incidenceId={incidence.id} />
}

export default observer(IncidenceContent)
